import type { SelectedOptions } from '@corratech/pylot-variant-selector'
import { useOptionSelector } from '@corratech/pylot-variant-selector'
import type {
    ConfigurableAttributeOption,
    ConfigurableProductOptionsValues,
    ConfigurableVariant,
    Maybe,
    MediaGalleryInterface,
    SimpleProduct,
    BundleItem
} from '@pylot-data/fwrdschema'
import type { ConfigurableProductOptions } from '@pylot-data/pylotschema'
import { useTranslation } from 'next-i18next'
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { ProductType } from './use-product-fetch'
import { getButtonLabel } from './utils/getButtonLabel'
import { getMediaGalleryEntries } from './utils/getMediaGalleryEntries'
import {
    SelectedBundleProductsAllTypes,
    SelectedBundleProductsValue
} from '@components/BundleProduct/type/type'
import { ButtonLabel } from './utils/pdpProductLabels'
import { getMediaGalleryWithParentProduct } from '@pylot-data/hooks/product/utils/getMediaGalleryWithParentProduct'
export { ButtonLabel } from './utils/pdpProductLabels'

// Redefining here, as importing original enum as a value from (fwrd/pylot)schema does not work
export enum ProductStockStatus {
    InStock = 'IN_STOCK',
    OutOfStock = 'OUT_OF_STOCK'
}

export enum OptionType {
    VisualSwatch = 'VisualSwatch',
    TextSwatch = 'TextSwatch',
    Dropdown = 'Dropdown',
    Other = 'Other'
}

export type Option = {
    displayName: string
    code: string
    values: Maybe<ConfigurableProductOptionsValues[]>
    type: OptionType | null
}

export enum ProductVariant {
    NOT_EXIST = -1,
    NOT_SELECTED = -2
}

type SelectedBundleProducts = { [key: string]: SelectedBundleProductsValue }

export type UseProductUIReturn<ProductType> = {
    setSelectedOptions: Dispatch<SetStateAction<SelectedOptions>>
    selectedOptions: SelectedOptions
    options: Option[]
    variantId: number
    variant: SimpleProduct | ProductType
    mediaGalleryEntries: Array<Maybe<MediaGalleryInterface>>
    buttonLabel: string
    isButtonDisabled: boolean
    canUpdateBundlePrice: boolean
    isConfigOptionSelected: boolean
    selectedOptionUIDs: string[]
    quantity: number
    setQuantity: Dispatch<SetStateAction<number>>
    selectedBundleProducts: SelectedBundleProducts
    setSelectedBundleProducts: (
        SelectedBundleProducts: SelectedBundleProductsAllTypes,
        selectedGallery?: Maybe<Maybe<MediaGalleryInterface>[]>
    ) => void
}

export type UseProductUi<ProductType> = (
    product: ProductType,
    options?: {
        preselectedOptions?: Record<string, string>
        buttonActionLabel?: ButtonLabel
    }
) => UseProductUIReturn<ProductType>

export const useProductUI: UseProductUi<any> = (
    product,
    { preselectedOptions, buttonActionLabel = ButtonLabel.ADD_TO_CART } = {
        buttonActionLabel: ButtonLabel.ADD_TO_CART
    }
) => {
    const { t } = useTranslation(['common'])

    const [variantId, setVariantId] = useState<number>(
        ProductVariant.NOT_SELECTED
    )

    const [quantity, setQuantity] = useState<number>(1)

    const productType = product?.__typename as ProductType | undefined
    const isConfig = productType === ProductType.CONFIGURABLE_PRODUCT
    const isBundle = productType === ProductType.BUNDLE_PRODUCT
    const [selectedBundleProducts, setSelectedBundle] = useState({})
    const [selectedGallery, setSelectedGallery] = useState<
        Maybe<Maybe<MediaGalleryInterface>[]>
    >([])
    const variant = product?.variants?.[variantId]?.product ?? product

    const { options, selectedOptions, setSelectedOptions } = useOptionSelector(
        product?.configurable_options as ConfigurableProductOptions[],
        preselectedOptions
    )
    const setSelectedBundleProducts = (
        bundleItem: SelectedBundleProductsAllTypes,
        selectedGallery: Maybe<Maybe<MediaGalleryInterface>[]> | undefined
    ) => {
        if (selectedGallery) {
            setSelectedGallery(selectedGallery)
        }
        setSelectedBundle(bundleItem)
    }

    const isConfigOptionSelected =
        isConfig && variantId === ProductVariant.NOT_SELECTED

    const { attributes } = product?.variants?.[variantId] ?? {}

    const getSelectedOptionUIDs = isBundle
        ? (Object.values(
              selectedBundleProducts
          ) as SelectedBundleProductsValue[])
              .reduce((acc: string[], i) => {
                  if (typeof i.uid === 'string') {
                      acc.push(i.uid)
                  } else {
                      return [...acc, ...i.uid.map((i) => i?.multiSelectUid)]
                  }
                  return acc
              }, [])
              .filter((i) => i)
        : (attributes as ConfigurableAttributeOption[])?.map(
              (attr) => attr?.uid ?? ''
          ) ?? []

    let canUpdateBundlePrice = false,
        isInstock = true
    const getIsButtonDisabled = () => {
        if (isBundle) {
            const isRequiredItemInCart = (BundleItem: BundleItem) => {
                const uid = (selectedBundleProducts as SelectedBundleProducts)[
                    BundleItem?.uid as string
                ]?.uid
                if (!uid) return false
                if (uid === 'string') {
                    return true
                } else {
                    if (uid.length) return true
                }
            }

            canUpdateBundlePrice = product.items?.every((item: BundleItem) => {
                return item.required
                    ? isRequiredItemInCart(item as BundleItem)
                    : true
            })

            isInstock =
                (product as SimpleProduct)?.stock_status ===
                ProductStockStatus.OutOfStock

            return (
                !getSelectedOptionUIDs.length ||
                isInstock ||
                !canUpdateBundlePrice
            )
        }

        isInstock =
            (product as SimpleProduct)?.stock_status ===
                ProductStockStatus.OutOfStock ||
            variant?.stock_status === ProductStockStatus.OutOfStock ||
            variantId === ProductVariant.NOT_EXIST

        return (
            isInstock || (isConfig && variantId === ProductVariant.NOT_SELECTED)
        )
    }

    //Component mount hook || option change
    useEffect(() => {
        const currentlySelectedOptions = Object.values(selectedOptions).filter(
            (v) => !!v
        )

        const areAllOptionsSelected =
            currentlySelectedOptions.length ===
            Object.keys(selectedOptions).length

        // if no configurable product
        if (!product?.variants || !areAllOptionsSelected) {
            setVariantId(ProductVariant.NOT_SELECTED)
        } else {
            const newVariantId = (product.variants as ConfigurableVariant[]).findIndex(
                (prodVar) => {
                    const { attributes: variantAttributes } = prodVar
                    return (variantAttributes as ConfigurableAttributeOption[]).every(
                        (attr) =>
                            selectedOptions[
                                attr.code as keyof SelectedOptions
                            ] === attr.label
                    )
                }
            )

            setVariantId(newVariantId)
        }
    }, [product, selectedOptions])

    //get the media galleries to render the gallery component
    const mediaGalleryEntries = useMemo(() => {
        const galleryEntries = getMediaGalleryEntries(product, variantId).map(
            (galleryItem) => {
                if (galleryItem?.url === variant.thumbnail?.url) {
                    // updating the thumbnail to first position
                    galleryItem!.position = -1
                }
                return galleryItem
            }
        )
        if (isBundle) {
            if (selectedGallery?.length) {
                return getMediaGalleryWithParentProduct(
                    selectedGallery as MediaGalleryInterface[],
                    galleryEntries
                )
            }
        }
        return galleryEntries
    }, [product, variantId, selectedGallery])

    const isButtonDisabled = getIsButtonDisabled()

    //get button text status
    const buttonLabel = useMemo(() => {
        const label = getButtonLabel(
            isInstock,
            buttonActionLabel,
            isButtonDisabled
        )
        return t(label)
        // TODO: check if that brings any benefit
    }, [
        buttonActionLabel,
        isInstock,
        t,
        canUpdateBundlePrice,
        isButtonDisabled
    ])

    return {
        setSelectedOptions,
        selectedOptions,
        setQuantity,
        quantity,
        options,
        variantId,
        variant,
        mediaGalleryEntries,
        buttonLabel,
        isButtonDisabled,
        canUpdateBundlePrice,
        isConfigOptionSelected,
        selectedOptionUIDs: getSelectedOptionUIDs,
        selectedBundleProducts,
        setSelectedBundleProducts
    }
}
